import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { isApiClientError, isValidationApiError } from "../api/errors";
import type { DocumentWorkflowDocumentInput } from "../entities/document-workflow";
import { AttachmentPicker, type PickedAttachment } from "./AttachmentPicker";
import {
  documentCreateSearchSchema,
  priorities,
  priorityLabels,
  types,
  typeLabels,
  type DocumentType,
  type Priority,
} from "./documentWorkflowSearch";
import type { ShellServices } from "./services";
import { Select } from "./Select";
import { Notice } from "./Notice";

interface DocumentFormState {
  type: DocumentType;
  subject: string;
  description: string;
  priority: Priority;
  counterparty_id: string;
  category_id: string;
  folder_id: string;
  incoming_number: string;
  document_date: string;
  received_at: string;
  due_at: string;
}

const emptyForm: DocumentFormState = {
  type: "incoming",
  subject: "",
  description: "",
  priority: "normal",
  counterparty_id: "",
  category_id: "",
  folder_id: "",
  incoming_number: "",
  document_date: "",
  received_at: "",
  due_at: "",
};

// Поля даты и времени приходят из браузера в локальном формате без зоны,
// поэтому переводим их в ISO с явным смещением — так сервер не гадает о зоне.
function timestamp(value: string): string | undefined {
  if (value === "") return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function text(value: string): string | undefined {
  return value === "" ? undefined : value;
}

function buildDocumentInput(
  form: DocumentFormState,
  attachments: readonly PickedAttachment[],
): DocumentWorkflowDocumentInput {
  return {
    ...(attachments.length === 0
      ? {}
      : {
          attachments: attachments.map((item) => ({
            file_id: item.id,
            display_name: item.filename,
            ...(item.signedVersionId === undefined
              ? {}
              : { signed_version_id: item.signedVersionId }),
          })),
        }),
    type: form.type,
    subject: form.subject.trim(),
    priority: form.priority,
    ...(text(form.description) === undefined
      ? {}
      : { description: form.description }),
    ...(text(form.counterparty_id) === undefined
      ? {}
      : { counterparty_id: form.counterparty_id }),
    ...(text(form.category_id) === undefined
      ? {}
      : { category_id: form.category_id }),
    ...(text(form.folder_id) === undefined
      ? {}
      : { folder_id: form.folder_id }),
    ...(text(form.incoming_number) === undefined
      ? {}
      : { incoming_number: form.incoming_number }),
    ...(text(form.document_date) === undefined
      ? {}
      : { document_date: form.document_date }),
    ...(timestamp(form.received_at) === undefined
      ? {}
      : { received_at: timestamp(form.received_at) }),
    ...(timestamp(form.due_at) === undefined
      ? {}
      : { due_at: timestamp(form.due_at) }),
  };
}

// Общая ошибка формы. Ошибки по конкретным полям сервер отдаёт отдельно,
// в error.details.fields, и они показываются рядом с полями.
function formError(error: unknown): string | null {
  if (error === null) return null;
  if (isValidationApiError(error)) {
    return "Сервер отклонил данные. Проверьте отмеченные поля.";
  }
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Недостаточно прав для создания документа.";
    if (error.status === 409)
      return "Документ уже создан. Обновите реестр, прежде чем повторять.";
    if (error.status === 429)
      return "Слишком много запросов. Подождите и повторите.";
    if (error.status === 503)
      return "Сервис документооборота временно недоступен.";
    return `${error.message} (${error.code})`;
  }
  return "Документ не создан. Повторите попытку.";
}

function fieldErrors(error: unknown): Record<string, string> {
  if (!isValidationApiError(error)) return {};
  const messages: Record<string, string> = {};
  for (const [field, values] of Object.entries(error.details.fields)) {
    const first = values[0];
    if (first !== undefined) messages[field] = first;
  }
  return messages;
}

export function DocumentCreatePage({ services }: { services: ShellServices }) {
  const search: unknown = useSearch({ strict: false });
  const { signed_version_id: versionId, signed_file_id: fileId } =
    documentCreateSearchSchema.parse(search);
  const source = useQuery({
    queryKey: ["document-workflow", "send-signed-version", fileId, versionId],
    enabled: versionId !== undefined,
    retry: false,
    queryFn: async () => {
      if (!fileId || !versionId)
        throw new Error("Не выбрана подписанная версия.");
      const versions = await services.documentWorkflow.fileVersions(fileId);
      const version = versions.find((item) => item.id === versionId);
      if (
        !version ||
        version.signature_state !== "signed" ||
        version.scan_status !== "clean" ||
        !version.available_actions.includes("download") ||
        !version.file_id ||
        !version.document_file_id
      ) {
        throw new Error("Подписанная версия недоступна для отправки.");
      }
      const file = await services.documentWorkflow.documentFile(
        version.document_file_id,
      );
      return {
        id: version.file_id,
        filename: file.display_name,
        size: version.size ?? 0,
        versionNumber: version.version_number,
        signedVersionId: version.id,
      } satisfies PickedAttachment;
    },
  });

  if (versionId && !source.data) {
    return (
      <section
        className="workspace-page dw-page"
        aria-label="Создание документа"
      >
        {source.isError ? (
          <Notice>
            Не удалось прикрепить подписанный файл. Проверьте доступ к исходному
            документу и повторите попытку.
            <button
              className="button button--secondary"
              type="button"
              onClick={() => void source.refetch()}
            >
              Повторить
            </button>
          </Notice>
        ) : (
          <p role="status">Прикрепляем подписанный файл…</p>
        )}
        <Link
          className="button button--text"
          to="/modules/document-workflow/documents"
        >
          К реестру
        </Link>
      </section>
    );
  }

  return (
    <DocumentCreateForm
      key={versionId ?? "empty"}
      services={services}
      initialAttachment={source.data}
    />
  );
}

function DocumentCreateForm({
  services,
  initialAttachment,
}: {
  services: ShellServices;
  initialAttachment?: PickedAttachment;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DocumentFormState>(emptyForm);
  const [attachments, setAttachments] = useState<readonly PickedAttachment[]>(
    () => (initialAttachment ? [initialAttachment] : []),
  );

  const categories = useQuery({
    queryKey: ["document-workflow", "categories"],
    queryFn: () => services.documentWorkflow.categories(),
    staleTime: 60_000,
  });
  const folders = useQuery({
    queryKey: ["document-workflow", "folders"],
    queryFn: () => services.documentWorkflow.folders(),
    staleTime: 60_000,
  });
  const counterparties = useQuery({
    queryKey: ["document-workflow", "counterparties"],
    queryFn: () => services.documentWorkflow.counterparties(),
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: (input: DocumentWorkflowDocumentInput) =>
      services.documentWorkflow.createDocument(input),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({
        queryKey: ["document-workflow"],
      });
      // Сразу открываем карточку созданного документа поверх реестра.
      await navigate({
        to: "/modules/document-workflow/documents",
        search: { document_id: created.id },
      });
    },
  });

  const errors = fieldErrors(create.error);
  const general = formError(create.isError ? create.error : null);

  function update(patch: Partial<DocumentFormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate(buildDocumentInput(form, attachments));
  }

  function fieldError(name: string) {
    const message = errors[name];
    if (message === undefined) return null;
    return (
      <small className="field-error" role="alert">
        {message}
      </small>
    );
  }

  return (
    <section
      className="workspace-page dw-page"
      aria-labelledby="dw-create-title"
    >
      <div className="dw-heading">
        <div>
          <p className="eyebrow">Документооборот</p>
          <h1 id="dw-create-title">Создание документа</h1>
          <p>
            Документ создаётся в текущем КПО и сразу попадает в реестр со
            статусом «Поступил».
          </p>
        </div>
        <Link
          className="button button--secondary"
          to="/modules/document-workflow/documents"
        >
          К реестру
        </Link>
      </div>

      <form className="dw-panel" onSubmit={submit} noValidate>
        {general === null ? null : <Notice>{general}</Notice>}

        <div className="dw-filter-grid">
          <label className="field">
            <span>Тип</span>
            <Select
              value={form.type}
              onChange={(event) =>
                update({ type: event.target.value as DocumentType })
              }
            >
              {types.map((type) => (
                <option key={type} value={type}>
                  {typeLabels[type]}
                </option>
              ))}
            </Select>
            {fieldError("type")}
          </label>
          <label className="field">
            <span>Приоритет</span>
            <Select
              value={form.priority}
              onChange={(event) =>
                update({ priority: event.target.value as Priority })
              }
            >
              {priorities.map((priority) => (
                <option key={priority} value={priority}>
                  {priorityLabels[priority]}
                </option>
              ))}
            </Select>
            {fieldError("priority")}
          </label>
        </div>

        <label className="field">
          <span>Тема</span>
          <input
            value={form.subject}
            maxLength={998}
            required
            onChange={(event) => update({ subject: event.target.value })}
          />
          {fieldError("subject")}
        </label>

        <label className="field">
          <span>Описание</span>
          <textarea
            value={form.description}
            rows={4}
            onChange={(event) => update({ description: event.target.value })}
          />
          {fieldError("description")}
        </label>

        <div className="dw-filter-grid">
          <label className="field">
            <span>Контрагент</span>
            <Select
              value={form.counterparty_id}
              onChange={(event) =>
                update({ counterparty_id: event.target.value })
              }
            >
              <option value="">Не выбран</option>
              {(counterparties.data ?? []).map((counterparty) => (
                <option key={counterparty.id} value={counterparty.id}>
                  {counterparty.name}
                </option>
              ))}
            </Select>
            {fieldError("counterparty_id")}
          </label>
          <label className="field">
            <span>Рубрика</span>
            <Select
              value={form.category_id}
              onChange={(event) => update({ category_id: event.target.value })}
            >
              <option value="">Не выбрана</option>
              {(categories.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
            {fieldError("category_id")}
          </label>
          <label className="field">
            <span>Папка</span>
            <Select
              value={form.folder_id}
              onChange={(event) => update({ folder_id: event.target.value })}
            >
              <option value="">Не выбрана</option>
              {(folders.data ?? []).map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </Select>
            {fieldError("folder_id")}
          </label>
        </div>

        <div className="dw-filter-grid">
          <label className="field">
            {/* Номер, под которым документ ушёл от отправителя: в API это
                incoming_number. */}
            <span>Исходящий номер</span>
            <input
              value={form.incoming_number}
              maxLength={160}
              onChange={(event) =>
                update({ incoming_number: event.target.value })
              }
            />
            {fieldError("incoming_number")}
          </label>
          <label className="field">
            <span>Дата документа</span>
            <input
              type="date"
              value={form.document_date}
              onChange={(event) =>
                update({ document_date: event.target.value })
              }
            />
            {fieldError("document_date")}
          </label>
          <label className="field">
            <span>Дата получения</span>
            <input
              type="datetime-local"
              value={form.received_at}
              onChange={(event) => update({ received_at: event.target.value })}
            />
            {fieldError("received_at")}
          </label>
          <label className="field">
            <span>Срок исполнения</span>
            <input
              type="datetime-local"
              value={form.due_at}
              onChange={(event) => update({ due_at: event.target.value })}
            />
            {fieldError("due_at")}
          </label>
        </div>

        <AttachmentPicker
          allowRename
          services={services}
          attachments={attachments}
          onChange={setAttachments}
          disabled={create.isPending}
        />

        <div className="dw-form-actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={create.isPending || form.subject.trim() === ""}
          >
            {create.isPending ? "Создаём..." : "Создать документ"}
          </button>
          <button
            className="button button--text"
            type="button"
            disabled={create.isPending}
            onClick={() => setForm(emptyForm)}
          >
            Очистить
          </button>
        </div>
      </form>
    </section>
  );
}
